import React, {useState} from 'react'
import {BlockProps} from '../atoms/blockList'
import {CodeSnippetValue} from './types'
import {I18nWrapper} from '../atoms/i18nWrapper'
import {Button, Drawer, Form, Input, Panel, SelectPicker} from 'rsuite'
import sanitizeHtml from 'sanitize-html'
import {useTranslation} from 'react-i18next'
import './codeSnippetBlock.less'

export type CodeSnippetBlockProps = BlockProps<CodeSnippetValue>

const aspectRatios = ['16:9', '5:8', '4:3', '1:1', '3:4', '8:5', '9:16'].map((item) => ({
  label: item,
  value: 'aspectratio_' + item.replace(':', '_'),
}))

const layouts = [
  {
    label: 'Aspect Ratio',
    value: 'aspectRatio',
  },
]

function sanitize(dirty: string) {
  return sanitizeHtml(dirty, {
    allowedTags: [
      'address',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'dd',
      'div',
      'dl',
      'dt',
      'hr',
      'li',
      'main',
      'ol',
      'p',
      'pre',
      'ul',
      'a',
      'abbr',
      'b',
      'br',
      'dfn',
      'em',
      'i',
      'kbd',
      'mark',
      'q',
      'rb',
      'rp',
      'rt',
      's',
      'samp',
      'small',
      'span',
      'strong',
      'sub',
      'sup',
      'time',
      'u',
      'var',
      'wbr',
      'caption',
      'col',
      'colgroup',
      'table',
      'tbody',
      'td',
      'tfoot',
      'th',
      'thead',
      'tr',
      'iframe',
    ],
    allowedAttributes: {
      td: ['bgcolor'],
      a: ['href', 'target'],
      iframe: ['src', 'width', 'height', 'title'],
    },
  })
}

function addSnippetStyles(html: string) {
  if (!html) return html
  const clean = sanitize(html)
  return clean.replace(/<iframe(\s|>)/gi, (match, p1) => {
    if (/style\s*=/.test(match)) {
      return match.replace(/style\s*=\s*"/i, 'style="border-radius:6px; overflow:hidden; ')
    }
    return `<iframe style="border-radius:6px; overflow:hidden; width:100%; height:100%; border:none;"${p1}`
  })
}

export function CodeSnippetBlock({
  value,
  onChange,
  languageLaneL,
  languageLaneR,
}: CodeSnippetBlockProps) {
  const {snippet, size} = value
  const {t} = useTranslation()
  const [isChooseModalOpen, setChooseModalOpen] = useState(false)

  return (
    <>
      <Button size={'md'} appearance={'ghost'} onClick={() => setChooseModalOpen(true)}>
        {'Edit iFrame'}
      </Button>
      <br />
      <br />
      <I18nWrapper
        lane1={
          <div
            style={{
              position: 'relative',
              width: '100%',
            }}>
            <div
              style={{
                position: 'absolute',
                zIndex: 1,
                height: '100%',
                left: 0,
              }}></div>
            <div
              style={{
                position: 'relative',
                paddingTop: '56.25%',
                width: '100%',
              }}>
              {
                <div
                  className="snippet-wrapper"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    cursor: 'not-allowed',
                    borderRadius: 6,
                    overflow: 'hidden',
                  }}
                  dangerouslySetInnerHTML={{__html: addSnippetStyles(snippet[languageLaneL] || '')}}
                />
              }
            </div>
          </div>
        }
        lane2={
          languageLaneR ? (
            <div
              style={{
                position: 'relative',
                width: '100%',
                borderRadius: 6,
                opacity: 0.7,
                overflow: 'hidden',
                cursor: 'not-allowed',
              }}>
              <div
                style={{
                  position: 'relative',
                  paddingTop: '56.25%',
                  width: '100%',
                }}>
                {
                  <div
                    className="snippet-wrapper"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: 6,
                      overflow: 'hidden',
                      pointerEvents: 'none',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: addSnippetStyles(snippet[languageLaneR] || ''),
                    }}
                  />
                }
              </div>
            </div>
          ) : null
        }
      />

      <Drawer
        open={isChooseModalOpen}
        style={{overflow: 'hidden'}}
        placement={'bottom'}
        size="full"
        onClose={() => setChooseModalOpen(false)}>
        <Drawer.Header>
          <Drawer.Title>{t('blockList.codeSnippet')}</Drawer.Title>
          <Drawer.Actions>
            <Button
              appearance={'primary'}
              color={'green'}
              onClick={() => {
                setChooseModalOpen(false)
                // persist the snippet with injected iframe styles
                onChange(addSnippetStyles(snippet[languageLaneL] || ''), ['snippet', languageLaneL])
              }}>
              {t('blockList.apply')}
            </Button>
          </Drawer.Actions>
        </Drawer.Header>
        <Drawer.Body style={{overflow: 'auto'}}>
          <I18nWrapper
            lane1={
              <Form>
                <div style={{marginBottom: 20}}>
                  <p>
                    <strong>{t('blocks.snippet.panels.noteTitle')}</strong>
                    {t('blocks.snippet.panels.note')}
                  </p>
                  <p>
                    <strong>{t('blocks.snippet.panels.instructions')}</strong>
                  </p>
                  <ul>
                    <li>{t('blocks.snippet.panels.supported_platforms')}</li>
                    <li>{t('blocks.snippet.panels.iframe_usage')}</li>
                    <li>{t('blocks.snippet.panels.interactive_content')}</li>
                  </ul>
                  <p>
                    <strong>{t('blocks.snippet.panels.sanitizeTitle')}</strong>
                    {t('blocks.snippet.panels.sanitize')}
                  </p>
                </div>

                <Form.Group controlId="snippet">
                  <Input
                    as="textarea"
                    rows={10}
                    value={snippet[languageLaneL] || ''}
                    onChange={(val) => onChange(val, ['snippet', languageLaneL])}
                    onBlur={() => {
                      onChange(addSnippetStyles(snippet[languageLaneL] || ''), [
                        'snippet',
                        languageLaneL,
                      ])
                    }}
                  />
                  <br />
                  <br />
                  <Button
                    onClick={() => {
                      onChange(addSnippetStyles(snippet[languageLaneL] || ''), [
                        'snippet',
                        languageLaneL,
                      ])
                    }}>
                    Sanitize iFrame
                  </Button>
                </Form.Group>
                <Form.Group controlId="layout">
                  <Form.ControlLabel>Layout</Form.ControlLabel>
                  <SelectPicker
                    data={layouts}
                    value={size?.aspectRatio ? 'aspectRatio' : undefined}
                    searchable={false}
                    onChange={(val) => {
                      if (val === 'aspectRatio') {
                        onChange(
                          {
                            aspectRatio: {
                              desktop: aspectRatios[0].value,
                              mobile: aspectRatios[0].value,
                            },
                          },
                          ['size']
                        )
                      } else {
                        onChange(undefined, ['size'])
                      }
                    }}
                  />
                </Form.Group>
                {size?.aspectRatio && (
                  <>
                    <Form.Group controlId="desktop">
                      <Form.ControlLabel>Aspect Ratio Desktop</Form.ControlLabel>
                      <SelectPicker
                        data={aspectRatios}
                        value={size?.aspectRatio?.desktop}
                        cleanable={false}
                        searchable={false}
                        onChange={(val) => {
                          onChange(
                            {
                              aspectRatio: {
                                ...size?.aspectRatio,
                                desktop: val,
                              },
                            },
                            ['size']
                          )
                        }}
                      />
                    </Form.Group>
                    <Form.Group controlId="mobile">
                      <Form.ControlLabel>Aspect Ratio Mobile</Form.ControlLabel>
                      <SelectPicker
                        data={aspectRatios}
                        value={size?.aspectRatio?.mobile}
                        cleanable={false}
                        searchable={false}
                        onChange={(val) => {
                          onChange(
                            {
                              aspectRatio: {
                                ...size?.aspectRatio,
                                mobile: val,
                              },
                            },
                            ['size']
                          )
                        }}
                      />
                    </Form.Group>
                  </>
                )}
              </Form>
            }
            lane2={
              languageLaneR ? (
                <Input as="textarea" rows={10} value={snippet[languageLaneR]} disabled={true} />
              ) : null
            }></I18nWrapper>
        </Drawer.Body>
      </Drawer>
    </>
  )
}
